import dayjs from "dayjs";
import { FC, useEffect, useTransition } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer } from "@ryde/layout";
import {
  useModalConfirm,
  usePageContext,
  useRidesActions,
  useRidesBulkActions,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  bulkCloneFormDrawerAF,
  ConfirmResult,
  IRideDto,
  selectedTableRowsIdsAtomFamily,
  tableRowsAtomFamily,
} from "@ryde/shared";

import { BulkCloneForm, bulkCloneFormValidationRules } from "./parts";

const DEFAULT_NUMBER_OF_DUPLICATION = 1;

interface IBulkCloneRidesForm {
  dateFrom: TDateISO;
  dateTo: TDateISO;
  dayGroup: number[];
  dayType: number[];
  numberOfDuplicates: number;
  journeyId: number;
}

export const BulkCloneFormRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { confirmDialogResult } = useModalConfirm();
  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const rideRows = useRecoilValue(tableRowsAtomFamily(entity)) as
    IRideDto[] | undefined;
  const isOneRideSelected = selectedRideIds.length === 1;
  const [, startTransition] = useTransition();
  const { fetchRides } = useRidesActions();

  const setBulkCloneFormDrawerOpened = useSetRecoilState(
    bulkCloneFormDrawerAF(entity),
  );

  const resolver = useYupValidationResolver(
    bulkCloneFormValidationRules,
    entity,
  );
  const methods = useForm<IBulkCloneRidesForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver,
    defaultValues: {
      numberOfDuplicates: DEFAULT_NUMBER_OF_DUPLICATION,
    },
  });
  const {
    getValues,
    trigger,
    setValue,
    formState: { isValid },
  } = methods;
  const { bulkCloneRides } = useRidesBulkActions();

  const onSaveClick = async (): Promise<void> => {
    await trigger();

    if (!isValid) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.CLONE_RIDES_MESSAGE`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const {
      dateFrom,
      dateTo,
      dayGroup,
      dayType,
      numberOfDuplicates,
      journeyId,
    } = getValues();

    await bulkCloneRides({
      ids: selectedRideIds,
      dateFrom: dateFrom && dayjs(dateFrom).toISOString(),
      dateTo: dateTo && dayjs(dateTo).toISOString(),
      dayGroup: dayGroup ?? [],
      dayType: dayType ?? [],
      numberOfDuplicates: numberOfDuplicates ?? 1,
      journeyId: journeyId ?? null,
    });
    startTransition(() => {
      fetchRides();
    });

    setBulkCloneFormDrawerOpened(false);
  };

  const onCloseClick = (): void => {
    setBulkCloneFormDrawerOpened(false);
  };

  useEffect(() => {
    if (!isOneRideSelected) {
      return;
    }

    const selectedRideId = selectedRideIds.at(0);

    if (!selectedRideId) {
      return;
    }

    const rideDto = rideRows?.find(({ id }) => selectedRideId === id);

    if (!rideDto?.journeyId) {
      return;
    }

    setValue("journeyId", rideDto.journeyId, {
      shouldDirty: true,
    });
  }, [isOneRideSelected, rideRows, selectedRideIds, setValue]);

  return (
    <BaseDrawer onCloseDrawer={onCloseClick}>
      <FormDrawer
        onCloseDrawer={onCloseClick}
        onCancelClick={onCloseClick}
        title={t("general.DUPLICATE_RIDES")}
        onSaveClick={onSaveClick}
        footerSaveLabel={t("general.CLONE")}
      >
        <FormProvider {...methods}>
          <BulkCloneForm isOneRideSelected={isOneRideSelected} />
        </FormProvider>
      </FormDrawer>
    </BaseDrawer>
  );
};
